/**
 * Project Detail Page
 *
 * Displays project details and list of requests with proper RequestCard component.
 * Uses TanStack Query hooks backed by the API server.
 */

import type {
  RequestPriority as DbRequestPriority,
  RequestStatus as DbRequestStatus,
} from '@cortex/database';
import {
  Button,
  Card,
  Center,
  Container,
  EmptyState,
  Grid,
  Heading,
  Inline,
  Input,
  RequestCard,
  Section,
  Skeleton,
  Stack,
  Surface,
  Text,
} from '@cortex/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { RequestCardSkeleton } from '@/components/skeletons';
import {
  useProjectBySlug,
  useProjectRequests,
} from '@/hooks/use-project-queries';
import {
  mapDbPriorityToUiPriority,
  mapDbStatusToUiStatus,
} from '@/lib/request-mappers';

// Search params validation
const projectSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  type: z.string().optional(),
  priority: z.string().optional(),
  sortBy: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
});

export const Route = createFileRoute('/_app/_main/projects/$slug')({
  validateSearch: projectSearchSchema,
  component: ProjectDetailPage,
});

const SEARCH_DEBOUNCE_MS = 300;

function ProjectDetailPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState(search.q ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(search.q ?? '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch project via API
  const {
    data: project,
    isPending: isLoadingProject,
    error: projectError,
  } = useProjectBySlug(slug);

  // Fetch requests for this project via API
  const {
    data: requests,
    isPending: isLoadingRequests,
    error: requestsError,
  } = useProjectRequests(project?.id);

  // Filter and sort requests
  const filteredRequests = useMemo(() => {
    if (!requests) return [];
    let result = [...requests];

    // Apply search
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter((r) =>
        r.title.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (search.status && search.status !== 'all') {
      result = result.filter((r) => r.status === search.status);
    }
    if (search.type && search.type !== 'all') {
      result = result.filter((r) => r.type === search.type);
    }
    if (search.priority && search.priority !== 'all') {
      result = result.filter((r) => r.priority === search.priority);
    }

    return result;
  }, [requests, debouncedSearch, search.status, search.type, search.priority]);

  // Handle search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearch('');
  }, []);

  const handleBack = () => navigate({ to: '/' });

  const handleNewRequest = () => {
    if (project) {
      navigate({ to: '/requests/new', search: { projectId: project.id } });
    }
  };

  const handleRequestClick = (requestNumber: number) => {
    navigate({ to: '/requests/$id', params: { id: String(requestNumber) } });
  };

  if (isLoadingProject) {
    return (
      <Stack gap="lg" className="p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </Stack>
    );
  }

  if (projectError || !project) {
    return (
      <Center minHeight="50vh">
        <Stack align="center">
          <Heading level={1} className="text-xl font-semibold">
            {projectError?.message ?? 'Project not found'}
          </Heading>
          <Button onClick={handleBack} variant="link" className="mt-4">
            Go back
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="none" className="min-h-screen">
      {/* Header */}
      <Surface as="header" variant="bordered" className="bg-surface-1">
        <Container maxWidth="7xl" className="py-6">
          <Stack gap="md">
            <Button
              onClick={handleBack}
              variant="ghost"
              className="text-foreground/60 hover:text-foreground flex items-center gap-2 self-start text-base"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>

            <Inline justify="between" align="start">
              <Stack gap="xs">
                <Heading level={1} className="text-2xl font-bold">
                  {project.name}
                </Heading>
                {project.description && (
                  <Text color="secondary" className="mt-2">
                    {project.description}
                  </Text>
                )}
              </Stack>
              <Button onClick={handleNewRequest}>
                <Plus className="mr-2 h-4 w-4" />
                New Request
              </Button>
            </Inline>
          </Stack>
        </Container>
      </Surface>

      {/* Main Content */}
      <Surface as="main">
        <Container maxWidth="7xl" className="py-8">
          <Section
            title={
              requests
                ? `Requests (${filteredRequests.length}${filteredRequests.length !== requests.length ? ` of ${requests.length}` : ''})`
                : 'Requests'
            }
          >
            {/* Search */}
            {requests && requests.length > 0 && (
              <Card className="flex items-center gap-3 p-4">
                <Inline className="max-w-[300px] min-w-[200px] flex-1">
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search requests..."
                    leftAddon={<Search className="h-4 w-4" />}
                    rightAddon={
                      searchQuery ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={clearSearch}
                          className="h-5 w-5 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : undefined
                    }
                  />
                </Inline>
              </Card>
            )}

            {/* Loading */}
            {isLoadingRequests && (
              <Grid gap="md" className="sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <RequestCardSkeleton key={i} />
                ))}
              </Grid>
            )}

            {/* Error */}
            {requestsError && (
              <Card className="p-4">
                <Text color="error" size="sm">
                  Failed to load requests: {requestsError.message}
                </Text>
              </Card>
            )}

            {/* Empty */}
            {!isLoadingRequests && !requestsError && requests?.length === 0 && (
              <EmptyState
                type="no-data"
                title="No requests yet"
                description="Create your first request to get started."
                actionLabel="Create Request"
                onAction={handleNewRequest}
              />
            )}

            {/* Requests Grid */}
            {!isLoadingRequests &&
              !requestsError &&
              filteredRequests.length > 0 && (
                <Grid gap="md" className="sm:grid-cols-2 lg:grid-cols-3">
                  {filteredRequests.map((request) => (
                    <RequestCard
                      key={request.id}
                      title={request.title}
                      {...(request.description != null && {
                        description: request.description,
                      })}
                      status={mapDbStatusToUiStatus(
                        request.status as DbRequestStatus
                      )}
                      priority={mapDbPriorityToUiPriority(
                        request.priority as DbRequestPriority
                      )}
                      requestId={`#${request.request_number}`}
                      onCardClick={() =>
                        handleRequestClick(request.request_number)
                      }
                    />
                  ))}
                </Grid>
              )}

            {/* No results */}
            {!isLoadingRequests &&
              !requestsError &&
              requests &&
              requests.length > 0 &&
              filteredRequests.length === 0 && (
                <EmptyState
                  type="no-results"
                  title="No matching requests"
                  description="Try adjusting your search or filters."
                  actionLabel="Clear search"
                  onAction={clearSearch}
                />
              )}
          </Section>
        </Container>
      </Surface>
    </Stack>
  );
}
